import React, {useMemo} from "react";
import {BlockProps, CustomInputRinforzoValue} from "./types";
import {useIntl} from "react-intl";
import {
  CLASSE_CLS_OPTIONS, CUT_RESISTANCE_DEFAULT,
  ENVIRONMENTAL_EXPOSURE_OPTIONS,
  FIELDS,
  SYSTEM_OPTIONS,
  TENSILE_CURB_OPTIONS,
  TIPOLOGIA_OPTIONS,
  WALL_FLOOR_CONNECTION_OPTIONS
} from "./lib/consts";
import Select from "@ui-components/Select";
import Input from "@ui-components/Input";
import ConfiguratorSurveyBlockHeader from "@components/ConfiguratorSurveyBlockHeader";
import ConfiguratorSurveyBoolQuestion from "@components/ConfiguratorSurveyBoolQuestion";
import _ from "lodash";
import {ConnettoriCustomInputProps} from "../types";

const Block = ({title, subtitle, children}: BlockProps) => (
  <div>
    {/*@ts-ignore*/}
    <ConfiguratorSurveyBlockHeader
      type="question"
      title={title}
      description={subtitle}
      required
    />
    {children}
  </div>
)

export const CustomInputConnettoriRinforzo = ({
  value,
  onChange,
  role,
}: ConnettoriCustomInputProps<CustomInputRinforzoValue>) => {

  // hooks
  const intl = useIntl();

  // utilities
  const update = (key: string, newValue: any, option?: boolean, removeKeys?: string[]) => {
    const v = _.omit({...(value || {}), [key]: newValue,}, removeKeys || []);
    onChange(v as CustomInputRinforzoValue, option);
  }

  const filteredReinforcementOptions = useMemo(() => {
    // TODO remove this temporary logic once the client asks for it
    // 2026-01-12 - Client asked to temporarily hide the following reinforcements options if the user is not internal:
    //              gmxsfh, gnsfh, mr4sfh
    // 2026-02-02 - Client asked to re-show gmxsfh and gnsfh to external users. So now only mr4sfh is hidden for
    //              external users.
    let options: Record<string, string> = {...TIPOLOGIA_OPTIONS};
    const internalRoles = ["admin", "agent", "area_manager", "field", "sales", "CS", "sales_director", "field_manager"];
    if (!internalRoles.includes(role))
      options = _.omit(options, ["mr4sfh"]);
    return options;
  }, [role]);

  return (
    <div className="flex flex-col gap-8">

      {/* -------------------------------------------------- */}
      {/*                      RINFORZO                      */}
      {/* -------------------------------------------------- */}

      <Block title={intl.formatMessage(FIELDS.tipologia_rinforzo.localization)}>
        {/*@ts-ignore*/}
        <Select
          options={[
            { name: "", value: "" },
            ...Object.entries(filteredReinforcementOptions) //TIPOLOGIA_OPTIONS)  // TODO temporary filtering - see useMemo above
              .map(([value, name]) => ({name, value})),
          ]}
          value={value?.tipologia_rinforzo || ""}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const reset = ["diametro_armatura_b450c", "passo_rete", "classe_cls"];
            update("tipologia_rinforzo", e.target.value, true, reset);
          }}
        />
      </Block>

      <Block
        title={intl.formatMessage(FIELDS.spessore_soletta.localization)}
        subtitle={intl.formatMessage({id: "slab_thickness_mm_description", defaultMessage: "È possibile inserire un valore minimo di 20 mm"})}
      >
        {/*@ts-ignore*/}
        <Input
          value={value?.spessore_soletta}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            update("spessore_soletta", parseFloat(e.target.value));
          }}
          type="number"
          validation={(v: number) => v >= 200}
          errorMessage={intl.formatMessage(
            {id: "insert_min_value", defaultMessage: "Inserire un valore maggiore o uguale a 200"},
            {value: 200},
          )}
          min={200}
          hideBottomMargin
        />
      </Block>

      {["gmre", "other"].includes(value?.tipologia_rinforzo || "") && <>
        <Block title={intl.formatMessage(FIELDS.diametro_armatura_b450c.localization)}>
          {/*@ts-ignore*/}
          <Input
            value={value?.diametro_armatura_b450c}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              update("diametro_armatura_b450c", parseFloat(e.target.value));
            }}
            type="number"
            min={0}
            hideBottomMargin
          />
        </Block>
        <Block title={intl.formatMessage(FIELDS.passo_rete.localization)}>
          {/*@ts-ignore*/}
          <Input
            value={value?.passo_rete}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              update("passo_rete", parseFloat(e.target.value));
            }}
            type="number"
            min={0}
            hideBottomMargin
          />
        </Block>
      </>}

      {value?.tipologia_rinforzo === "other" && (
        <Block title={intl.formatMessage(FIELDS.classe_cls.localization)}>
          {/*@ts-ignore*/}
          <Select
            options={[
              { name: "", value: "" },
              ...CLASSE_CLS_OPTIONS
                .map((v) => ({name: v, value: v})),
            ]}
            value={value?.classe_cls || ""}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              update("classe_cls", e.target.value, true);
            }}
          />
        </Block>
      )}

      <Block
        title={intl.formatMessage({id: "need_to_add_b450c_bars_prompt", defaultMessage: "È necessario aggiungere barre B450C?"})}
        subtitle={intl.formatMessage({id: "need_to_add_b450c_bars_hint", defaultMessage: "Barre aggiuntive per assorbire gli sforzi di tensione del tirante T2"})}
      >
        <ConfiguratorSurveyBoolQuestion
          value={!!value?.barre_b450c_rinforzo}
          onClick={(v: boolean) => {
            onChange({
              ...(value || {}),
              barre_b450c_rinforzo: v ? 1 : undefined,
              diametro_barre_b450c_rinforzo: undefined,
            } as CustomInputRinforzoValue);
          }}
        />
      </Block>

      <div className="flex justify-center">
        <img
          className="w-1/2 2xl:w-1/3"
          src="/images/connettori/rinforzo.png"
          alt="Guida rinforzo"
        />
      </div>

      {!!value?.barre_b450c_rinforzo && <>
        <Block title={intl.formatMessage(FIELDS.barre_b450c_rinforzo.localization)}>
          {/*@ts-ignore*/}
          <Input
            value={value?.barre_b450c_rinforzo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              update("barre_b450c_rinforzo", parseInt(e.target.value));
            }}
            type="number"
            min={1}
            hideBottomMargin
          />
        </Block>
        <Block title={intl.formatMessage(FIELDS.diametro_barre_b450c_rinforzo.localization)}>
          {/*@ts-ignore*/}
          <Input
            value={value?.diametro_barre_b450c_rinforzo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              update("diametro_barre_b450c_rinforzo", parseFloat(e.target.value));
            }}
            type="number"
            min={0}
            hideBottomMargin
          />
        </Block>
      </>}

      <hr className="border-gray-300" />

      {/* ------------------------------------------------- */}
      {/*                      CORDOLO                      */}
      {/* ------------------------------------------------- */}

      <Block title={intl.formatMessage(FIELDS.tipologia_cordolo.localization)}>
        {/*@ts-ignore*/}
        <Select
          options={[
            { name: "", value: "" },
            ...Object.entries(TENSILE_CURB_OPTIONS)
              .map(([value, name]) => ({name, value})),
          ]}
          value={value?.tipologia_cordolo || ""}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const reset = [
              "sistema_cordolo",
              "numero_strati_cordolo",
              "altezza_fascia_cordolo",
              "esposizione_ambientale",
              "barre_b450c_cordolo",
              "diametro_barre_b450c_cordolo",
            ];
            update("tipologia_cordolo", e.target.value, true, reset);
          }}
        />
      </Block>

      {value?.tipologia_cordolo === "srg" && <>
        <Block title={intl.formatMessage(FIELDS.sistema_cordolo.localization)}>
          {/*@ts-ignore*/}
          <Select
            options={[
              { name: "", value: "" },
              ...Object.entries(SYSTEM_OPTIONS)
                .map(([value, name]) => ({name, value})),
            ]}
            value={value?.sistema_cordolo || ""}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const reset = [
                "numero_strati_cordolo",
                "altezza_fascia_cordolo",
                "esposizione_ambientale",
              ];
              update("sistema_cordolo", e.target.value, true, reset);
            }}
          />
        </Block>
        {!!value?.sistema_cordolo && <>
          <Block title={intl.formatMessage(FIELDS.numero_strati_cordolo.localization)}>
            {/*@ts-ignore*/}
            <Input
              value={value?.numero_strati_cordolo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                update("numero_strati_cordolo", parseInt(e.target.value));
              }}
              type="number"
              min={1}
              hideBottomMargin
            />
          </Block>
          <Block title={intl.formatMessage(FIELDS.altezza_fascia_cordolo.localization)}>
            {/*@ts-ignore*/}
            <Input
              value={value?.altezza_fascia_cordolo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                update("altezza_fascia_cordolo", parseFloat(e.target.value));
              }}
              type="number"
              min={1}
              hideBottomMargin
            />
          </Block>
          <Block title={intl.formatMessage(FIELDS.esposizione_ambientale.localization)}>
            {/*@ts-ignore*/}
            <Select
              options={[
                { name: "", value: "" },
                ...ENVIRONMENTAL_EXPOSURE_OPTIONS
                  .map((v) => ({name: _.capitalize(v), value: v})),
              ]}
              value={value?.esposizione_ambientale || ""}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => update("esposizione_ambientale", e.target.value, true)}
            />
          </Block>
        </>}
      </>}

      {value?.tipologia_cordolo === "bb450c" && <>
        <Block title={intl.formatMessage(FIELDS.barre_b450c_cordolo.localization)}>
          {/*@ts-ignore*/}
          <Input
            value={value?.barre_b450c_cordolo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              update("barre_b450c_cordolo", parseInt(e.target.value));
            }}
            type="number"
            min={0}
            hideBottomMargin
          />
        </Block>
        <Block title={intl.formatMessage(FIELDS.diametro_barre_b450c_cordolo.localization)}>
          {/*@ts-ignore*/}
          <Input
            value={value?.diametro_barre_b450c_cordolo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              update("diametro_barre_b450c_cordolo", parseFloat(e.target.value));
            }}
            type="number"
            min={0}
            hideBottomMargin
          />
        </Block>
      </>}

      <hr />

      {/* ------------------------------------------------- */}
      {/*             CONNETTORE PARETE SOLAIO              */}
      {/* ------------------------------------------------- */}

      <Block title={intl.formatMessage(FIELDS.wall_floor_connector.localization)}>
        {/*@ts-ignore*/}
        <Select
          options={[
            { name: "", value: "" },
            ...Object.entries(WALL_FLOOR_CONNECTION_OPTIONS)
              .map(([value, name]) => ({name, value})),
          ]}
          value={value?.wall_floor_connector || ""}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            onChange({
              ...(value || {}),
              wall_floor_connector: e.target.value,
              resistenza_taglio: CUT_RESISTANCE_DEFAULT,
            } as CustomInputRinforzoValue)
          }}
        />
      </Block>

      {!!value?.wall_floor_connector && (
        <Block title={intl.formatMessage(FIELDS.resistenza_taglio.localization)}>
          {/*@ts-ignore*/}
          <Input
            value={value?.resistenza_taglio}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              update("resistenza_taglio", parseFloat(e.target.value));
            }}
            type="number"
            min={0}
            hideBottomMargin
            disabled={value?.wall_floor_connector === "scw_gfa"}
          />
        </Block>
      )}
    </div>
  )
}